/**
 * 書庫横断検索ウィジェット（FR-LSEARCH-03・最小実装）。
 *
 * 一覧ページ等に埋め込む検索ボックス＋結果リスト。/api/v1/search を叩き、許可ブックの
 * ヒット（ブック識別・件数・代表スニペット）を表示する。各結果はビューワの該当ブック・
 * 該当ページへのディープリンク（FR-LSEARCH-02）。認可はサーバの AuthAdapter が担うため
 * ここでは署名を扱わない。ビューワ本体とは独立して使える公開ファクトリ。
 */
import { cls } from '../brand';
import { createTranslator } from '../i18n/i18n';
import type { I18nOptions } from '../core/options';

const DEBOUNCE_MS = 250;

/** 横断検索のヒット（サーバ応答のブック単位）。 */
export interface LibraryBook {
  slug: string;
  title: string;
  hitCount: number;
  firstPage: number;
  snippet: string;
}

export interface LibrarySearchOptions {
  /** 横断検索 API（既定 '/api/v1/search'）。 */
  endpoint?: string;
  /** ヒットのブックを開くリンク先。既定は `?slug=<slug>#page=<firstPage>`。 */
  linkTo?: (book: LibraryBook) => string;
  /** 最低文字数（既定 2）。 */
  minChars?: number;
  /** 文言辞書の部分上書き（FR-I18N-01）。 */
  i18n?: Partial<I18nOptions>;
}

export interface LibrarySearchHandle {
  dispose(): void;
}

interface LibraryResponse {
  query: string;
  books: LibraryBook[];
}

/** 横断検索ウィジェットを要素へ生成する。 */
export function librarySearch(el: HTMLElement, options: LibrarySearchOptions = {}): LibrarySearchHandle {
  const doc = el.ownerDocument;
  const endpoint = options.endpoint ?? '/api/v1/search';
  const minChars = options.minChars ?? 2;
  const linkTo =
    options.linkTo ?? ((b: LibraryBook): string => `?slug=${encodeURIComponent(b.slug)}#page=${b.firstPage}`);
  const translate = createTranslator(
    { locale: options.i18n?.locale ?? 'auto', strings: options.i18n?.strings ?? {} },
    doc,
  );

  el.classList.add(cls('libsearch'));
  const input = doc.createElement('input');
  input.type = 'search';
  input.className = cls('libsearch-input');
  input.autocomplete = 'off';
  // name の無い欄は DevTools が指摘する（id は複数ウィジェットで衝突するので使わない）。
  input.name = 'q';
  input.setAttribute('aria-label', translate('search.label'));
  input.placeholder = translate('search.placeholder');

  const status = doc.createElement('p');
  status.className = cls('libsearch-status');
  status.setAttribute('aria-live', 'polite');

  const results = doc.createElement('ul');
  results.className = cls('libsearch-results');

  el.append(input, status, results);

  let timer: number | null = null;
  let controller: AbortController | null = null;

  const run = (query: string): void => {
    controller?.abort();
    if ([...query].length < minChars) {
      status.textContent = translate('search.minChars', { count: minChars });
      results.replaceChildren();
      return;
    }
    status.textContent = translate('search.searching');
    const ctrl = new AbortController();
    controller = ctrl;
    void fetch(endpoint + '?q=' + encodeURIComponent(query), {
      signal: ctrl.signal,
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? (response.json() as Promise<LibraryResponse>) : null))
      .then((data) => {
        if (ctrl.signal.aborted) return;
        render(data?.books ?? []);
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          status.textContent = translate('search.noResults');
          results.replaceChildren();
        }
      });
  };

  const render = (books: LibraryBook[]): void => {
    status.textContent =
      books.length === 0
        ? translate('search.noResults')
        : translate('search.results', { count: books.length });
    const fragment = doc.createDocumentFragment();
    for (const book of books) {
      const li = doc.createElement('li');
      const link = doc.createElement('a');
      link.className = cls('libsearch-hit');
      link.href = linkTo(book);

      const title = doc.createElement('span');
      title.className = cls('libsearch-hit-title');
      title.textContent = book.title;

      const meta = doc.createElement('span');
      meta.className = cls('libsearch-hit-meta');
      meta.textContent = translate('search.results', { count: book.hitCount });

      const snippet = doc.createElement('span');
      snippet.className = cls('libsearch-hit-snippet');
      snippet.textContent = book.snippet;

      link.append(title, meta, snippet);
      li.appendChild(link);
      fragment.appendChild(li);
    }
    results.replaceChildren(fragment);
  };

  const onInput = (): void => {
    const view = doc.defaultView;
    if (timer !== null) view?.clearTimeout(timer);
    const value = input.value.trim();
    if (value === '') {
      controller?.abort();
      status.textContent = '';
      results.replaceChildren();
      return;
    }
    timer = view?.setTimeout(() => run(value), DEBOUNCE_MS) ?? null;
  };
  input.addEventListener('input', onInput);

  return {
    dispose(): void {
      if (timer !== null) doc.defaultView?.clearTimeout(timer);
      controller?.abort();
      input.removeEventListener('input', onInput);
      el.classList.remove(cls('libsearch'));
      input.remove();
      status.remove();
      results.remove();
    },
  };
}
